import { RandomSourceWithSeed } from "./random";

/**
 * Returns a random color with the given red, green, and blue channels.
 * If the channels are not provided, the color will be a random color.
 * @param r red channel override 0 - 255
 * @param g green channel override 0 - 255
 * @param b blue channel override 0 - 255
 * @returns 0xRRGGBB
 */
export function randomColor(r?: number, g?: number, b?: number, seed?: number): number {
  const randomSource = new RandomSourceWithSeed(seed ?? Math.floor(Math.random() * 1000000));
  const rand = Math.floor(randomSource.next() * 0xffffff);
  r = r ?? (rand >> 16) & 0xff;
  g = g ?? (rand >> 8) & 0xff;
  b = b ?? rand & 0xff;
  return (r << 16) | (g << 8) | b;
}

export function modColor(color: number, r?: number, g?: number, b?: number): number {
  const rComp = (color >> 16) & 0xff;
  const gComp = (color >> 8) & 0xff;
  const bComp = color & 0xff;
  r = r !== undefined ? (r + rComp) % 255 : rComp;
  g = g !== undefined ? (g + gComp) % 255 : gComp;
  b = b !== undefined ? (b + bComp) % 255 : bComp;
  return (r << 16) | (g << 8) | b;
}

export function randomStableColor(seed: number): number {
  const randomSource = new RandomSourceWithSeed(seed);
  return Math.floor(randomSource.next() * 0xffffff);
}

export function numberColorToHex(color: number): string {
  return `#${color.toString(16).padStart(6, "0")}`;
}

export function randomStableColorHex(seed: number): string {
  return numberColorToHex(randomStableColor(seed));
}

export const HEX_COLOR_REGEX = /^#(?:[0-9A-Fa-f]{6})$/;

/**
 * Picks a dark or white text color for readability against a given hex background color, using
 * a perceived-brightness (YIQ-style) heuristic rather than full WCAG contrast-ratio math.
 */
export function getContrastTextColor(hex: string, fallbackBackground = "#000000", darkText = "#222222"): string {
  const resolvedHex = HEX_COLOR_REGEX.test(hex) ? hex : fallbackBackground;
  const red = Number.parseInt(resolvedHex.slice(1, 3), 16);
  const green = Number.parseInt(resolvedHex.slice(3, 5), 16);
  const blue = Number.parseInt(resolvedHex.slice(5, 7), 16);
  const brightness = (red * 299 + green * 587 + blue * 114) / 1000;

  return brightness > 160 ? darkText : "#ffffff";
}
